/**
 * useTestConnection — POST /enterprise/connectors/{conn_id}/test.
 *
 * Mutation-only (no cache key — the latency / version reading is
 * transient diagnostic data, not state). The result is surfaced
 * inline by the caller (EditConnectorDialog status chip,
 * SchemaViewer "Verifica connessione" footer).
 *
 * BE returns 200 with ``success: false`` for credential/network errors —
 * we do NOT throw on that branch, the caller branches on ``data.success``.
 *
 * Two modes (June 10, 2026):
 *   - ``{connId}`` — tests the SAVED state (create-time / "verify after
 *     persist" path used by SchemaViewer).
 *   - ``{connId, credentials}`` — tests a DRAFT (operator typed a new
 *     host/port/user in EditConnectorDialog but hasn't pressed "Salva"
 *     yet). BE discards the temporary connector after the probe — no
 *     persistence side effect.
 */

import { useMutation } from "@tanstack/react-query";

import { type ConnectionTestResponse, testEnterpriseConnection } from "@ando/core/api";

export interface UseTestConnectionVars {
  readonly connId: string;
  /**
   * Optional draft credentials. When provided, the BE tests these
   * values against the live datasource WITHOUT persisting them — the
   * saved connection row is untouched. Useful for "Verifica
   * connessione" before "Salva".
   */
  readonly credentials?: Readonly<Record<string, unknown>>;
}

export function useTestConnection() {
  return useMutation<ConnectionTestResponse, Error, UseTestConnectionVars>({
    mutationFn: ({ connId, credentials }) =>
      testEnterpriseConnection(connId, credentials ? { credentials } : undefined),
  });
}
